import { useEffect, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { AuthTokens, Customer, LineProfile } from '@/types'
import { api, hasRefreshToken, refreshTokens, setTokens, clearTokens } from '@/lib/api'
import { registerTenantPush, unregisterTenantPush, setupTenantPushRefreshOnResume } from '@/lib/push/tenantPush'
import { AuthCtx } from './authCtx'

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [customer, setCustomer] = useState<Customer | null>(null)
  const [lineProfile, setLineProfile] = useState<LineProfile | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const fetchCustomer = async () => {
    try {
      const response = await api.get<{ customer: Customer }>('/api/customer/session')
      setCustomer(response.data.customer)
      // Enable VConsole for test account in production
      if (localStorage.getItem('liff_lease_code') === 'E2ETEST1') {
        import('vconsole').then(({ default: VConsole }) => {
          new VConsole()
        })
      }
    } catch {
      setCustomer(null)
      clearTokens()
    }
  }

  // EP-156 T156-003/T156-005 (spec §7 S14, §8 A9) — bind the LINE user id to
  // this customer, once per app open and again right after OTP login. The
  // LIFF ID token is the only thing sent; a raw LINE user id from the
  // browser is never trusted (the server re-verifies the token against LINE
  // itself). Best-effort and silent by design: a 422
  // (id_token_invalid | line_login_not_configured), a network failure, or an
  // uninitialized LIFF SDK (the Capacitor shell, which never calls
  // liff.init()) all look the same here — nothing is ever shown to the
  // tenant, and a failure here must never block login or app open.
  const linkLineAccount = async () => {
    try {
      const liff = (await import('@line/liff')).default
      if (!liff.isLoggedIn()) return
      const idToken = liff.getIDToken()
      if (!idToken) return
      await api.post('/api/customer/line_link', { id_token: idToken })
    } catch {
      // Silent by design — see the note above.
    }
  }

  useEffect(() => {
    const init = async () => {
      try {
        const liff = (await import('@line/liff')).default
        if (liff.isLoggedIn()) {
          const profile = await liff.getProfile()
          setLineProfile(profile)
        }
      } catch {
        // LIFF profile not available
      }

      // LIFF-H3: access token lives in memory, so each app open restores the
      // session from the stored refresh token (single roundtrip).
      if (hasRefreshToken()) {
        try {
          await refreshTokens()
          await fetchCustomer()
          // Register for push on every app open too, not only after OTP
          // login: a tenant who updated the app while logged in would
          // otherwise never register (Peerapat 1.0.4, 2026-10-08). The
          // server upserts by token, so repeating it is harmless.
          void registerTenantPush()
        } catch {
          clearTokens()
        }
        await linkLineAccount()
      }
      setIsLoading(false)
    }
    init()
  }, [])

  // While signed in, coming back to the app re-registers the device too
  // (throttled in tenantPush), so the server's "opened in the last 30 days"
  // check reflects real use, not only fresh launches (owner, 2026-10-09).
  const signedIn = !!customer
  useEffect(() => {
    if (!signedIn) return
    let cleanup: (() => void) | undefined
    let cancelled = false
    void setupTenantPushRefreshOnResume().then((remove) => {
      if (cancelled) remove()
      else cleanup = remove
    })
    return () => {
      cancelled = true
      cleanup?.()
    }
  }, [signedIn])

  const login = async (tokens: AuthTokens) => {
    setTokens(tokens)
    await fetchCustomer()
    await linkLineAccount()
    // EP-156 T156-006: register the device for FCM push (native-shell-guarded,
    // best-effort — a no-op in LINE and a no-op without Firebase config).
    // Fire-and-forget, same as the operator app's registerOperatorPush():
    // the permission prompt must never stall the post-OTP navigation.
    void registerTenantPush()
  }

  const logout = async () => {
    // EP-156 T156-006: unregister the device token while the access token is
    // still valid (mirrors the operator app's unregisterOperatorPush()).
    await unregisterTenantPush()
    clearTokens()
    localStorage.removeItem('liff_lease_code')
    setCustomer(null)
    queryClient.clear()
  }

  return (
    <AuthCtx.Provider
      value={{
        isAuthenticated: !!customer,
        isLoading,
        customer,
        lineProfile,
        login,
        logout,
      }}
    >
      {children}
    </AuthCtx.Provider>
  )
}
